import { useCallback, useMemo, useState } from 'react';

import type { PrefetchableQueryHookResult } from '@change-corgi/core-react/async';
import { prefetchQuery, usePrefetchableQuery } from '@change-corgi/core-react/async';
import { createMandatoryContext } from '@change-corgi/core-react/context';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { HttpError, RedirectError } from 'src/shared/error';
import type { PrefetchContext } from 'src/shared/prefetch';

import { getInitialPrefetchUserDataQueryId, usePrefetchUserDataQueryId } from 'src/app/shared/hooks/query';
import { useMappedLoadedState } from 'src/app/shared/hooks/state';

import type { ContextData, Map, OptionsPrefetchable, ResultPrefetchable, RethrowableErrorState } from './types';
import { useCheckHookDeps } from './useCheckHookDeps';

function createContext<DATA extends Record<string, unknown>, DATA_PROPERTY extends string = 'data'>(
	name: string,
	dataProperty: DATA_PROPERTY,
) {
	return createMandatoryContext<
		ContextData<DATA>,
		Record<DATA_PROPERTY, ContextData<DATA>>
		// oxlint-disable-next-line @typescript-eslint/no-unsafe-argument,
	>(undefined, {
		name,
		processProviderProps: (props: Record<DATA_PROPERTY, ContextData<DATA>>): ContextData<DATA> => props[dataProperty],
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	} as any);
}

// oxlint-disable-next-line max-lines-per-function
export function createPrefetchableAsyncDataContext<
	DATA extends Record<string, unknown>,
	PARAMS extends unknown[] = [],
	DATA_PROPERTY extends string = 'data',
	ERROR extends Map = EmptyIntersection,
>({
	name,
	getUniqueId: getUniqueIdBase = () => '',
	getData,
	hasUserData,
	dataProperty: dataPropertyP,
	errorHandler: errorHandlerP,
	hookDeps,
	loadingBetweenQueries,
}: OptionsPrefetchable<DATA, PARAMS, DATA_PROPERTY, ERROR>): ResultPrefetchable<DATA, PARAMS, DATA_PROPERTY, ERROR> {
	const dataProperty = dataPropertyP || ('data' as DATA_PROPERTY);
	const { Context, Provider, useContext } = createContext<DATA, DATA_PROPERTY>(name, dataProperty);

	const useUniqueId = hasUserData
		? (...params: PARAMS) => {
				const id = getUniqueIdBase(...params);
				const queryId = usePrefetchUserDataQueryId();
				return `${id}:${queryId}`;
			}
		: (...params: PARAMS) => getUniqueIdBase(...params);

	const getUniqueId = hasUserData
		? (...params: PARAMS) => {
				const id = getUniqueIdBase(...params);
				const queryId = getInitialPrefetchUserDataQueryId();
				return `${id}:${queryId}`;
			}
		: (...params: PARAMS) => getUniqueIdBase(...params);

	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	const errorHandler: (e: any) => ERROR = (error: any) => {
		// not catching HTTP errors that should instead be handled by the server, or the ErrorBoundary
		if (error instanceof HttpError || error instanceof RedirectError) {
			throw error;
		}
		return errorHandlerP ? errorHandlerP(error) : ({} as ERROR);
	};
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	const hookErrorHandler: (e: any) => ERROR = (error: any) => {
		try {
			return errorHandler(error);
		} catch (err) {
			// using a "hidden" field in the returned state, because the error is thrown within a useEffect
			// which would not fall into a ErrorBoundary
			return { __errorToRethrow: err } satisfies RethrowableErrorState as unknown as ERROR;
		}
	};

	const res = {
		Context,
		Provider,
		useContext,
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		useAsyncData: (prefetchedState: any, ...params: PARAMS) => {
			const utilityContext = useUtilityContext();
			const track = useTracking();
			const utilityContextWithTracker = useMemo(() => ({ ...utilityContext, track }), [utilityContext, track]);
			const [refreshCount, setRefreshCount] = useState(0);

			const uniqueId = useUniqueId(...params);
			const uniqueIdWithRefresh = refreshCount ? `${uniqueId}:refresh_${refreshCount}` : uniqueId;

			const deps = hookDeps ? hookDeps(...params) : params;

			useCheckHookDeps(deps, utilityContext);

			const refreshData = useCallback(() => setRefreshCount((count) => count + 1), []);

			const dataState = usePrefetchableQuery(
				async () => ({
					[dataProperty]: await getData(utilityContextWithTracker, ...params),
				}),
				// oxlint-disable-next-line react-hooks/exhaustive-deps
				[...deps, uniqueIdWithRefresh, utilityContextWithTracker],
				{
					id: uniqueIdWithRefresh,
					// oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment
					prefetchedState,
					errorHandler: hookErrorHandler,
					loadingBetweenQueries,
				},
			);

			// oxlint-disable-next-line @typescript-eslint/no-explicit-any,
			const hookRes = useMappedLoadedState(dataState, (data: any) => ({
				[dataProperty]: {
					// oxlint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
					data: data[dataProperty],
					actions: {
						refreshData,
					},
				},
			})) as PrefetchableQueryHookResult<Record<DATA_PROPERTY, ContextData<DATA>>, ERROR>;

			if (hookRes.status === 'error' && (hookRes as unknown as RethrowableErrorState).__errorToRethrow) {
				throw (hookRes as unknown as RethrowableErrorState).__errorToRethrow;
			}

			return hookRes;
		},
		prefetchAsyncData: async (prefetchContext: PrefetchContext, ...params: PARAMS) => {
			return prefetchQuery(
				async () => ({
					[dataProperty || 'data']: await getData(
						{ ...prefetchContext.utilityContext, track: prefetchContext.track },
						...params,
					),
				}),
				{
					id: getUniqueId(...params),
					errorHandler,
				},
			);
		},
	};

	return res;
}
